import { useContext } from 'react';

import { LayoutActionsContext, LayoutStateContext } from './LayoutContext';
import type { LayoutActions, LayoutContextValue, LayoutState } from './types';

/**
 * Hook to access layout state only
 *
 * Use when you only need to read layout state without actions.
 * Throws if used outside LayoutProvider.
 */
export function useLayoutState(): LayoutState {
    const state = useContext(LayoutStateContext);

    if (state === null) {
        throw new Error(
            'useLayoutState must be used within a LayoutProvider. ' +
                'Make sure LayoutProvider is mounted higher in the component tree.'
        );
    }

    return state;
}

/**
 * Hook to access layout actions only
 *
 * Use when you only need to dispatch actions without reading state.
 * Throws if used outside LayoutProvider.
 */
export function useLayoutActions(): LayoutActions {
    const actions = useContext(LayoutActionsContext);

    if (actions === null) {
        throw new Error(
            'useLayoutActions must be used within a LayoutProvider. ' +
                'Make sure LayoutProvider is mounted higher in the component tree.'
        );
    }

    return actions;
}

/**
 * Main hook to access both layout state and actions
 *
 * Returns an object with `state` and `actions` properties.
 * Throws if used outside LayoutProvider.
 */
export function useLayout(): LayoutContextValue {
    const state = useLayoutState();
    const actions = useLayoutActions();

    return { state, actions };
}

/**
 * Hook to check if sidebar should be visible
 *
 * On mobile: returns sidebarOpen (drawer state)
 * On desktop: always returns true (sidebar is always visible, may be collapsed)
 */
export function useSidebarVisible(): boolean {
    const { sidebarOpen, isMobile } = useLayoutState();
    return isMobile ? sidebarOpen : true;
}

/**
 * Hook to check if sidebar is in collapsed mode
 *
 * Only relevant on desktop - returns false on mobile.
 */
export function useSidebarCollapsed(): boolean {
    const { sidebarCollapsed, isMobile } = useLayoutState();
    return isMobile ? false : sidebarCollapsed;
}

/**
 * Hook to get current breakpoint info
 *
 * Returns { isMobile, isTablet, isDesktop }
 */
export function useBreakpoint(): {
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
} {
    const { isMobile, isTablet, isDesktop } = useLayoutState();
    return { isMobile, isTablet, isDesktop };
}
